import { useState, useEffect } from 'react';
import axios from 'axios';
import { Image, Plus, Trash2, X, ImageIcon, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BoothPhotoGallery({ eventId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [photoType, setPhotoType] = useState('full_booth');
  const [viewPhoto, setViewPhoto] = useState(null);
  const [activePhotoTab, setActivePhotoTab] = useState('booth');

  useEffect(() => {
    fetchPhotos();
  }, [eventId]);

  const fetchPhotos = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/events/${eventId}/photos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhoto = async () => {
    if (!photoUrl) {
      toast.error('Please enter a photo URL');
      return;
    }

    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.post(
        `${BACKEND_URL}/api/events/${eventId}/photos?photo_url=${encodeURIComponent(photoUrl)}&caption=${encodeURIComponent(caption)}&photo_type=${photoType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Photo added');
      setDialogOpen(false);
      setPhotoUrl('');
      setCaption('');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to add photo');
      console.error(error);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/events/${eventId}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
      console.error(error);
    }
  };

  const photoTypes = [
    { value: 'full_booth', label: 'Full Booth' },
    { value: 'setup', label: 'Setup Process' },
    { value: 'products', label: 'Products Display' },
    { value: 'crowd', label: 'Crowd/Traffic' },
    { value: 'team', label: 'Team Photo' },
    { value: 'logo', label: 'Logo/Branding' },
    { value: 'marketing', label: 'Marketing Material' },
    { value: 'signage', label: 'Signage' },
    { value: 'other', label: 'Other' },
  ];

  // Separate booth photos from logos/branding
  const boothPhotos = photos.filter(p => !['logo', 'marketing', 'signage'].includes(p.photo_type));
  const brandingPhotos = photos.filter(p => ['logo', 'marketing', 'signage'].includes(p.photo_type));

  // Render photo grid
  const renderPhotoGrid = (photoList, emptyMessage) => {
    if (photoList.length === 0) {
      return (
        <Card className="p-8 text-center bg-secondary/20">
          <Image className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{emptyMessage}</p>
          <p className="text-xs text-muted-foreground mt-1">Click "Add Photo" to upload</p>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photoList.map((photo) => (
          <div
            key={photo.id}
            className="relative group cursor-pointer"
            onClick={() => setViewPhoto(photo)}
          >
            <div className="aspect-square rounded-lg overflow-hidden bg-secondary/30">
              <img
                src={photo.photo_url}
                alt={photo.caption || 'Photo'}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300?text=Image+Not+Found';
                }}
              />
            </div>
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
              <Button
                variant="destructive"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePhoto(photo.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {photo.caption && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{photo.caption}</p>
            )}
            <span className="text-xs text-primary capitalize">{photo.photo_type.replace('_', ' ')}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="text-muted-foreground text-center py-8">Loading photos...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Photos & Branding</h3>
        <Button onClick={() => setDialogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Photo
        </Button>
      </div>

      {/* Tabs for Booth Photos vs Logos/Branding */}
      <Tabs value={activePhotoTab} onValueChange={setActivePhotoTab}>
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="booth" className="text-sm">
            <ImageIcon className="h-4 w-4 mr-2" />
            Booth Photos ({boothPhotos.length})
          </TabsTrigger>
          <TabsTrigger value="branding" className="text-sm">
            <Palette className="h-4 w-4 mr-2" />
            Logos & Branding ({brandingPhotos.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="booth" className="mt-4">
          {renderPhotoGrid(boothPhotos, 'No booth photos yet')}
        </TabsContent>

        <TabsContent value="branding" className="mt-4">
          {renderPhotoGrid(brandingPhotos, 'No logos or branding materials yet')}
        </TabsContent>
      </Tabs>

      {/* Add Photo Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Add Booth Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="photoUrl">Photo URL *</Label>
              <Input
                id="photoUrl"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder="https://example.com/photo.jpg"
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste a direct link to an image (upload to Imgur, Google Drive, etc.)
              </p>
            </div>
            <div>
              <Label htmlFor="caption">Caption</Label>
              <Input
                id="caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Brief description of the photo"
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <div>
              <Label htmlFor="photoType">Photo Type</Label>
              <select
                id="photoType"
                value={photoType}
                onChange={(e) => setPhotoType(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-11"
              >
                {photoTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddPhoto}>Add Photo</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Photo Dialog */}
      <Dialog open={!!viewPhoto} onOpenChange={() => setViewPhoto(null)}>
        <DialogContent className="bg-card max-w-3xl">
          {viewPhoto && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-heading flex items-center justify-between">
                  <span>{viewPhoto.caption || 'Booth Photo'}</span>
                  <Button variant="ghost" size="sm" onClick={() => setViewPhoto(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <img
                  src={viewPhoto.photo_url}
                  alt={viewPhoto.caption || 'Booth photo'}
                  className="w-full rounded-lg"
                />
                <p className="text-sm text-muted-foreground mt-2 capitalize">
                  Type: {viewPhoto.photo_type.replace('_', ' ')}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
